<template>
  <div class="cooling-stages-stage">
    <s-input-label-edit
      :editable="editing"
      :disabled="disableField"
      :formatted-value="formatNumberValue(fieldOptions.precision, fieldOptions.precision)"
      :value="valueHandler"
      @input="setValueOnCoolingObject"
    >
      <s-select
        v-if="isCoolingTypeProperty"
        :options="coolingTypeOptions"
        :value="valueHandler"
        :state="isFieldInvalid"
        @input="setValueOnCoolingObject"
      />
      <s-number-input
        v-else
        v-model="valueHandler"
        :disabled="disableField"
        :validation="getValidation"
        :invalid="isFieldInvalid"
        :numeric-options="configureNumericOptionsPrecision(fieldOptions.precision)"
      />
      <b-form-invalid-feedback>
        {{ getRangeValueMessage() }}
      </b-form-invalid-feedback>
    </s-input-label-edit>
  </div>
</template>

<script>
import { coolingTypeMap } from 'sigma-mappings';
import coolingStageFieldsOptions from '@/domain/data/installation/assetInformation/coolingStages/coolingStageFieldsOptions';

export default {
  name: 'CoolingStagesField',
  props: {
    stage: {
      type: Number,
      required: true,
    },
    field: {
      type: String,
      default: '',
    },
    saving: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Number, String],
    },
    transformerCoolingStages: {
      type: Object,
      default: () => ({}),
    },
  },
  mounted() {
    this.emitFieldValidation(this.isFieldInvalid);
  },
  computed: {
    getValidation() {
      const { minValue, maxValue } = this.fieldOptions;
      return {
        $params: {
          minValue: {
            min: minValue,
          },
          maxValue: {
            max: maxValue,
          },
        },
      };
    },
    fieldOptions() {
      return coolingStageFieldsOptions[this.field] || {};
    },
    inputOptions() {
      return {
        ...this.fieldOptions,
        state: this.$bsStateValidator(this.isFieldInvalid),
      };
    },
    valueHandler: {
      get() {
        return this.value != null ? this.value : null;
      },
      set(value) {
        this.setValueOnCoolingObject(value);
      },
    },
    coolingTypeOptions() {
      const coolingTypeOptions = Object.entries(coolingTypeMap).map(item => ({
        text: item[1],
        value: item[1],
      }));
      return [
        {
          text: this.$t('general.select'),
          value: null,
        },
        ...coolingTypeOptions,
      ];
    },
    gradientXOilDisableRule() {
      const { monitoredWindingAmount } = this.transformerCoolingStages;
      return monitoredWindingAmount === 1;
    },
    gradientYOilDisableRule() {
      const { tertiaryWinding } = this.transformerCoolingStages;
      const { monitoredWindingAmount } = this.transformerCoolingStages;
      return !tertiaryWinding
        || monitoredWindingAmount === 1
        || monitoredWindingAmount === 2;
    },
    fieldsDisableRules() {
      return {
        windingGradientXOil: this.gradientXOilDisableRule,
        windingGradientYOil: this.gradientYOilDisableRule,
      };
    },
    disableField() {
      return this.fieldsDisableRules[this.field];
    },
    isMinValueInvalid() {
      return this.value < this.fieldOptions.minValue;
    },
    isMaxValueInvalid() {
      return this.value > this.fieldOptions.maxValue;
    },
    isFieldInvalid() {
      if (this.value == null) {
        if (this.field === 'nominalPotency' || this.field === 'coolingType') return true;
        return false;
      }

      return this.isMinValueInvalid || this.isMaxValueInvalid;
    },
    isCoolingTypeProperty() {
      return this.field === 'coolingType';
    },
  },
  methods: {
    getRangeValueMessage() {
      if (this.value == null) return '';
      const options = {
        minimumFractionDigits: this.fieldOptions.precision,
        maximumFractionDigits: this.fieldOptions.precision || 0,
      };
      return this.$t('messages.MA086.message', {
        minValue: this.$n(this.fieldOptions.minValue, options),
        maxValue: this.$n(this.fieldOptions.maxValue, options),
      });
    },
    configureNumericOptionsPrecision(decimalPlaces = 0) {
      return {
        decimalPlaces,
      };
    },
    formatNumberValue(minimumFractionDigits, maximumFractionDigits) {
      if (typeof this.value !== 'number' || this.isCoolingTypeProperty) {
        return this.value;
      }
      return this.$n(this.value, { minimumFractionDigits, maximumFractionDigits });
    },
    setValueOnCoolingObject(value) {
      this.$emit('input', this.stage, this.field, value);
    },
    emitFieldValidation(isInvalid) {
      const field = {
        name: `${this.field}${this.stage}`,
        stage: this.stage,
        invalid: isInvalid,
      };
      this.$emit('update:invalid-field', field);
    },
  },
  watch: {
    isFieldInvalid(newValue) {
      this.emitFieldValidation(newValue);
    },
  },
};

</script>
